<script setup lang="ts">
import { computed } from 'vue'
import {
  getIconBody,
  getBrandIconBody,
  hasIcon,
  hasBrandIcon,
  ICON_VIEWBOX,
  type IconName,
  type BrandIconName,
} from './icons'
import IconSvgBody from './IconSvgBody.vue'

interface Props {
  name: string
  size?: number | string
  strokeWidth?: number
  ariaLabel?: string
}

const props = withDefaults(defineProps<Props>(), {
  size: 18,
  strokeWidth: 2,
  ariaLabel: '',
})

const isBrandIcon = computed(() => hasBrandIcon(props.name))
const isKnown = computed(() => hasIcon(props.name) || isBrandIcon.value)
const body = computed(() => {
  if (hasIcon(props.name)) return getIconBody(props.name as IconName)
  if (hasBrandIcon(props.name)) return getBrandIconBody(props.name as BrandIconName)
  return ''
})
const dimension = computed(() => (typeof props.size === 'number' ? `${props.size}px` : props.size))
const role = computed(() => (props.ariaLabel ? 'img' : 'presentation'))
const ariaHidden = computed(() => (props.ariaLabel ? undefined : true))
const ariaLabelAttr = computed(() => props.ariaLabel || undefined)
</script>

<template>
  <svg
    v-if="isKnown"
    class="app-icon"
    :width="dimension"
    :height="dimension"
    :viewBox="ICON_VIEWBOX"
    :stroke-width="strokeWidth"
    :role="role"
    :aria-hidden="ariaHidden"
    :aria-label="ariaLabelAttr"
  >
    <IconSvgBody
      :html="body"
      :brand="isBrandIcon"
    />
  </svg>
</template>

<style scoped>
.app-icon {
  display: inline-block;
  vertical-align: middle;
  flex-shrink: 0;
}
</style>
